import React, { useState, useEffect } from "react";
import Filters from "../sortAndFilter/Filters";
import ProductCard from "../product/ProductCard";
import Pagination from "@mui/material/Pagination";
import styled from "styled-components";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppContext } from "../../context/useAppContext";
import { useRequest } from "../../hooks/useRequest";
import Sort from "../common/Sort";
import { staticUrl } from "../../utils/baseUrl";
import { useGeoLocation } from "../../hooks/useGeoLocation";

const FilterableProductsStyle = styled.div`
  .products__wrapper {
    padding-bottom: 60px;
    display: flex;
    gap: 38px;
    position: relative;
    aside {
      width: 290px;
      .products__filters {
        border-radius: 11.25px;
        border: 0.75px solid #d9d9d9;
        background: #fcfcfc;
        min-height: 600px;
      }
    }
    .products {
      flex: 1;
      .title {
        color: #303030;
        font-size: 27px;
        font-weight: 600;
        line-height: 34.5px;
        margin-bottom: 15px;
      }
      .products__sorting__wrapper {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      .subtitle {
        color: #303030;
        font-size: 12px;
        font-weight: 400;
        line-height: 18px;
      }
      .products__grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 7.5px;
        margin-top: 48px;
      }
      .product__list__pagination {
        margin-top: 35px;
        display: flex;
        justify-content: center;
      }
    }
  }

  .filter-skeleton {
    padding: 16px;
    .skeleton-title {
      height: 24px;
      background: #f0f0f0;
      border-radius: 4px;
      margin-bottom: 16px;
      width: 60%;
    }
    .skeleton-item {
      height: 20px;
      background: #f0f0f0;
      border-radius: 4px;
      margin-bottom: 12px;
      width: 80%;
      &:last-child {
        width: 40%;
      }
    }
  }

  .mobile-filter-button {
    display: none;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border: 1px solid #d9d9d9;
    border-radius: 8px;
    background: #fff;
    cursor: pointer;
    font-size: 14px;
    color: #303030;

    svg {
      width: 20px;
      height: 20px;
    }

    span {
      display: none;
    }
  }

  .mobile-filter-sidebar {
    display: none;
    position: fixed;
    top: 0;
    right: -100%;
    width: 100%;
    height: 100vh;
    z-index: 1000;
    transition: right 0.3s ease-in-out;

    &.open {
      right: 0;
    }

    .sidebar-content {
      position: absolute;
      top: 0;
      right: 0;
      width: 85%;
      max-width: 320px;
      height: 100%;
      background: #fff;
      padding: 20px;
      overflow-y: auto;
    }

    .close-button {
      position: absolute;
      top: 16px;
      right: 16px;
      background: none;
      border: none;
      cursor: pointer;
      padding: 8px;
      color: #303030;
      display: flex;
      align-items: center;
      justify-content: center;

      svg {
        width: 24px;
        height: 24px;
      }
    }
  }

  @media (max-width: 768px) {
    .products__wrapper {
      padding-top: 20px;
      padding-bottom: 20px;
      .products {
        .title {
          margin-bottom: 20px;
        }
        .products__sorting__wrapper {
          flex-direction: column;
          gap: 8px;
        }
        .products__grid {
          grid-template-columns: repeat(2, 1fr);
        }
      }
    }

    .mobile-filter-button {
      display: flex;
      span {
        display: none;
      }
    }

    .mobile-filter-sidebar {
      display: block;
    }

    aside {
      display: none;
    }
  }
`;

const ProductsNotFound = styled.div`
  text-align: center;
  padding: 50px 0;
  .sad__emoji {
    font-size: 100px;
  }
  .not__found {
    font-size: 24px;
    font-weight: 500;
  }
`;

const sortingOptions = [
  // {
  //   label: "Newest first",
  //   value: "newestFirst",
  //   sortOrder: "asc",
  // },
  {
    label: "Price (low to high)",
    value: "price",
    sortOrder: "asc",
  },
  {
    label: "Price (high to low)",
    value: "price",
    sortOrder: "desc",
  },
  {
    label: "Top Rated",
    value: "topRated",
    sortOrder: "dsc",
  },
  {
    label: "Best Seller",
    value: "bestSeller",
    sortOrder: "dsc",
  },
  // {
  //   label: "Name (A - Z)",
  //   value: "name",
  //   sortOrder: "asc",
  // },
  // {
  //   label: "Name (Z - A)",
  //   value: "name",
  //   sortOrder: "desc",
  // },
  // {
  //   label: "Date (Ascending)",
  //   value: "date",
  //   sortOrder: "asc",
  // },
  // {
  //   label: "Date (Descending)",
  //   value: "date",
  //   sortOrder: "desc",
  // },
];

const FilterSkeleton = () => (
  <div className="filter-skeleton">
    <div className="skeleton-title"></div>
    <div className="skeleton-item"></div>
    <div className="skeleton-item"></div>
    <div className="skeleton-item"></div>
    <div className="skeleton-item"></div>
    <div className="skeleton-item"></div>
  </div>
);

const FilterableProducts = ({
  categoriesData,
  brandsData,
  title = "Products",
  setCategoriesList,
  type=""
}) => {
  const [searchInput, setSearchInput] = useState({ type: "", value: "" });
  const [fetchProducts, { isLoading: isFetchingProducts, state: products }] =
    useRequest();
  const navigate = useNavigate();
  const { isDesktop, categoriesLoading } = useAppContext();
  const [categoriesIdList, setCategoriesIdList] = useState();
  const [brandsIdList, setBrandsIdList] = useState();
  const { search } = useLocation();
  const [pageNumber, setPageNumber] = useState(1);
  const [sortBy, setSortBy] = useState(sortingOptions[0]?.value);
  const [sortOrder, setSortOrder] = useState("");
  const [sortLabel, setSortLabel] = useState(sortingOptions[0]?.label);
  const [priceRange, setPriceRange] = useState();
  const [locationRange, setLocationRange] = useState();
  const [initialLoading, setInitialLoading] = useState(true);
  const { userLocation: latLong, enableLocation } = useGeoLocation();
  const [srh, setSrh] = useState("");
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const [instabuildParams, setInstabuild] = useState("");
  const [clearanceParams, setClearance] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(search);
    const srh = params.get("search");

    const instaBuild = params.getAll("instabuild");
    const clearance = params.get("clearance");

    const categoryNames = params.getAll("categories[]");
    const brandNames = params.getAll("brands[]");
    setCategoriesIdList(categoryNames); 
    setTimeout(() => {
      setInitialLoading(false);
    }, 100);
    
    setBrandsIdList(brandNames);
    setInstabuild(instaBuild);
    setClearance(clearance);
    setSrh(srh);
    setCategoriesList && setCategoriesList(categoryNames);
  }, [search]); // Remove categoriesData and brandsData dependencies

  const generateURL = (
    pageNumber,
    categories,
    brands,
    sortBy,
    sortOrder,
    priceRange,
    locationRange,
    latLong,
    instabuildParams,
    clearanceParams,
    srh
  ) => {
    let url = `/product?limit=40&page=${pageNumber}`;
    categories.forEach((category) => {
      url += `&categories[]=${category}`;
    });
    brands?.forEach((brand) => {
      url += `&brands[]=${brand}`;
    });
    if (type) {
      url += `&type=${type}`;
    }
    // Clearance takes precedence over instabuild if both are present
    if (clearanceParams === "true") {
      url += `&type=clearance`;
    } else if (instabuildParams.length > 0) {
      url += `&type=instabuild`;
    }
    if (sortBy && sortOrder && sortBy !== "newestFirst") {
      url += `&sortBy=${sortBy}&sortOrder=${sortOrder}`;
    }
    if (priceRange && priceRange?.length) {
      url += `&from_price=${priceRange?.[0]}&to_price=${priceRange?.[1]}`;
    }
    if (srh) {
      url += `&search=${srh}`;
    }
    if (
      locationRange &&
      locationRange?.length &&
      latLong &&
      latLong?.latitude &&
      latLong?.longitude
    ) {
      url += `&startKm=${locationRange?.[0]}&endKm=${locationRange?.[1]}&latitude=${latLong?.latitude}&longitude=${latLong?.longitude}`;
    }
    return url;
  };

  useEffect(() => {
    if (!initialLoading) {
      const path = generateURL(
        pageNumber,
        categoriesIdList,
        brandsIdList,
        sortBy,
        sortOrder,
        priceRange,
        locationRange,
        latLong,
        instabuildParams,
        clearanceParams,
        srh
      );
      fetchProducts({ path });
    }
    // eslint-disable-next-line
  }, [
    pageNumber,
    categoriesIdList,
    brandsIdList,
    sortBy,
    sortOrder,
    priceRange,
    locationRange,
    latLong,
    instabuildParams,
    clearanceParams,
    srh,
    initialLoading
  ]);

  const handleProductClick = (item) => {
    let slug = item?.slug;
    if (slug.endsWith(".")) {
      slug = slug.slice(0, -1);
    }
    navigate(`/product/${slug}?id=${item?._id}`);
  };

  const handlePaginationChange = (e, value) => {
    setPageNumber(value);
  };

  const handleSortClick = (item) => {
    if (item?.value === sortBy && item?.sortOrder === sortOrder) {
      return;
    }
    setSortBy(item?.value);
    setSortOrder(item?.sortOrder);
    setSortLabel(item?.label);
  };

  const handleSidebarClick = (e) => {
    if (e.target === e.currentTarget) {
      setShowMobileFilters(false);
    }
  };

  return (
    <FilterableProductsStyle>
      <section className="products__wrapper">
        {isDesktop && (
          <aside>
            <div className="products__filters">
              {categoriesLoading ? (
                <FilterSkeleton />
              ) : (
                  <Filters
                    categoriesData={categoriesData}
                    brandsData={brandsData}
                    searchInput={searchInput}
                    setSearchInput={setSearchInput}
                    setPriceRange={setPriceRange}
                    setLocationRange={setLocationRange}
                    enableLocation={enableLocation}
                    userLocation={latLong}
                  />
              )}
            </div>
          </aside>
        )}
        <div className="products">
          <h2 className="title">{title}</h2>
          <div className="products__sorting__wrapper">
            <p className="subtitle">
              Showing 1 - {products?.data?.docs?.length || 0} of{" "}
              {products?.data?.totalDocs || 0} results.
            </p>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
              {!isDesktop && (
                <button
                  className="mobile-filter-button"
                  onClick={() => setShowMobileFilters(true)}
                >
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 18H14V16H10V18ZM3 6V8H21V6H3ZM6 13H18V11H6V13Z" fill="currentColor" />
                  </svg>
                  <span>Filters</span>
                </button>
              )}
              <Sort
                onSortClick={handleSortClick}
                label={sortLabel}
                sortingOptions={sortingOptions}
              />
            </div>
          </div>
          <div className="products__grid">
            {products?.data?.docs?.map((product) => (
              <ProductCard
                key={product?._id}
                isLoading={isFetchingProducts || initialLoading}
                productTitle={product?.name}
                productImage={`${staticUrl}/${product.image}`}
                onClick={() => handleProductClick(product)}
                ratingCount={product?.numReviews}
                avgRating={product?.avgRating}
                productDiscountedPrice={product?.discountedPrice}
                productPrice={product?.price}
                discountRequired={product?.discount_required}
                {...product}
              />
            ))}
            {isFetchingProducts &&
              Array.from({ length: 12 }, (_, index) => index + 1)?.map(
                (item) => <ProductCard key={item} isLoading={true} />
              )}
          </div>
          {!isFetchingProducts && products?.data?.docs?.length <= 0 && (
            <ProductsNotFound>
              <span className="sad__emoji">😞</span>
              <h2 className="not__found">No Products Found!</h2>
            </ProductsNotFound>
          )}
          {!isFetchingProducts && products?.data?.docs?.length > 0 && (
            <div className="product__list__pagination">
              <Pagination
                className="pagination"
                count={products?.data?.totalPages}
                shape="rounded"
                onChange={handlePaginationChange}
              />
            </div>
          )}
        </div>
      </section>

      <div
        className={`mobile-filter-sidebar ${showMobileFilters ? 'open' : ''}`}
        onClick={handleSidebarClick}
      >
        <div className="sidebar-content">
          <button
            className="close-button"
            onClick={() => setShowMobileFilters(false)}
          >
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="currentColor" />
            </svg>
          </button>
          {categoriesLoading ? (
            <FilterSkeleton />
          ) : (
            <Filters
              categoriesData={categoriesData}
              brandsData={brandsData}
              searchInput={searchInput}
              setSearchInput={setSearchInput}
              setPriceRange={setPriceRange}
              setLocationRange={setLocationRange}
              enableLocation={enableLocation}
              userLocation={latLong}
            />
          )}
        </div>
      </div>
    </FilterableProductsStyle>
  );
};

export default FilterableProducts;
